/**
 * Cash & Bank — where the company's money is, who holds it, and how it moved.
 *
 * Each balance is the account's opening balance plus every movement that
 * landed in it: the account a payment named, else its location's default for
 * the method. Every source — customer payments, remittance, loans and their
 * repayments, expenses, rider payouts and cash returns, investor payouts,
 * transfers — reaches exactly one account, read from the record that caused
 * it, so nothing is posted twice. Money no account claims is shown as
 * unassigned, by source and location, rather than left out.
 *
 * Kept apart and never in a balance: cash a rider holds until they hand it
 * over, bank payments still awaiting verification, and deposits that have
 * left a cash account but not been confirmed at the bank ("in transit").
 * Banking cash is a transfer, never revenue; a transfer entered in error is
 * voided with a reason and stays on the list.
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
  MONEY_ACCOUNT_KINDS,
  MONEY_ACCOUNT_KIND_LABELS,
  type CashBankAccountView,
  type CashBankDashboard,
  type CashBankPeriodFigures,
  type MoneyAccountKind,
  type MoneyAccountView,
  type MoneyTransferView,
} from '@cenox/shared';
import { Badge, Button, Card, EmptyState, ErrorState, Input, Modal, Select, TableSkeleton, cn } from '../../components/ui';
import { GeoScopeFields } from '../../components/GeoScope';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useAreaFilter, useLocationReady, useWorkingLocation } from '../../lib/locationContext';
import { money, todayIn } from '../../lib/format';
import { addAmounts, subtractAmounts } from '@cenox/shared';
import { newSubmissionKey } from '../loans/data';
import { CashFlowCard, JournalModal, StatementsCard } from './CashReports';
import { TransactionHistory } from './TransactionHistory';
import { AwaitingConfirmation, Collections, ConfirmOrDecline, MyCash, MyCashPage, Position, TRANSFER_KIND_LABEL } from './Custody';
import {
  useAccountMovements,
  useCashBankDashboard,
  useCreateAccount,
  useMemberOptions,
  useRecordTransfer,
  useStatements,
  useTransfers,
  useUpdateAccount,
  useUploadEvidence,
  useVoidTransfer,
  type MemberOption,
} from './data';

import { dayLabel, SOURCE_LABELS } from './labels';

export { SOURCE_LABELS };

const firstOfMonth = (iso: string): string => `${iso.slice(0, 7)}-01`;

export function MoneyAccountsPage(): ReactNode {
  const { can } = useAuth();
  // A worker who hands over cash but does not read the business's figures sees only their own.
  return can('reports:read') ? <CashBankPage /> : <MyCashPage />;
}

function CashBankPage(): ReactNode {
  const { business, can } = useAuth();
  const timezone = business?.timezone ?? 'UTC';
  const today = todayIn(timezone);
  const [from, setFrom] = useState(firstOfMonth(today));
  const [to, setTo] = useState(today);
  const [accountId, setAccountId] = useState('');
  const [holderUserId, setHolder] = useState('');
  const area = useAreaFilter();
  const mayManage = can('cash:reconcile');
  const mayDeposit = mayManage || can('cash:deposit');
  const filters = useMemo(
    () => ({
      from,
      to: to < from ? from : to,
      ...area,
      ...(accountId ? { accountId } : {}),
      ...(holderUserId ? { holderUserId } : {}),
    }),
    [from, to, area, accountId, holderUserId],
  );
  const dashboard = useCashBankDashboard(filters, useLocationReady());
  const transfers = useTransfers();
  const members = useMemberOptions(mayManage);
  const [creating, setCreating] = useState(false);
  // Moving money; a panel's Record button picks the kind of account it goes to.
  const [moving, setMoving] = useState<{ toKind?: 'cash' | 'bank' } | null>(null);
  const { locations } = useWorkingLocation();
  // The history reads the page's filters; its own location picker narrows them further.
  const historyScope = useMemo(
    () => ({ ...area, ...(accountId ? { accountId } : {}), ...(holderUserId ? { holderUserId } : {}) }),
    [area, accountId, holderUserId],
  );
  const [viewing, setViewing] = useState<CashBankAccountView | null>(null);
  const [journal, setJournal] = useState(false);
  const [opening, setOpening] = useState<MoneyAccountView | null>(null);

  // Started now, not when the dashboard has answered: each section loads on its own.
  useStatements();
  const data = dashboard.data;
  const currency = data?.currencyCode ?? business?.currencyCode ?? 'NGN';
  const m = (v: string) => money(v, currency);
  const live = (data?.accounts ?? []).filter((a) => !a.archived);
  const holders = [
    ...new Map((data?.accounts ?? []).filter((a) => a.holderUserId).map((a) => [a.holderUserId!, a.holderName ?? 'Worker'])).entries(),
  ];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Cash &amp; bank</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Where the company&apos;s money is and who holds it, from every payment in and out. Revenue is not here — only
            money that moved.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => setJournal(true)}>Journal</Button>
          {mayManage ? <Button variant="secondary" onClick={() => setCreating(true)}>Add account</Button> : null}
          {mayDeposit ? <Button onClick={() => setMoving({})} disabled={live.length < 2}>Move money</Button> : null}
        </div>
      </header>

      <Card>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7" data-testid="cash-bank-filters">
          <Input label="From" type="date" value={from} max={today} onChange={(e) => e.target.value && setFrom(e.target.value)} />
          <Input label="To (balances as of)" type="date" value={to} max={today} onChange={(e) => e.target.value && setTo(e.target.value)} />
          <GeoScopeFields bare idPrefix="cash-bank-scope" />
          <Select
            label="Account"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            options={[{ value: '', label: 'All accounts' }, ...(data?.accounts ?? []).map((a) => ({ value: a.id, label: a.name }))]}
          />
          <Select
            label="Worker"
            value={holderUserId}
            onChange={(e) => setHolder(e.target.value)}
            options={[{ value: '', label: 'All workers' }, ...holders.map(([id, name]) => ({ value: id, label: name }))]}
          />
        </div>
      </Card>

      {dashboard.isError ? (
        <ErrorState message="Could not load Cash & Bank." onRetry={() => void dashboard.refetch()} />
      ) : null}
      {data ? (
        <>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid="cash-bank-totals">
            <CashPanel
              data={data}
              onRecord={mayDeposit && live.length >= 2 ? () => setMoving({ toKind: 'cash' }) : undefined}
            />
            <BankPanel
              data={data}
              onRecord={mayDeposit && live.length >= 2 ? () => setMoving({ toKind: 'bank' }) : undefined}
            />
          </div>

          <Attention data={data} />
        </>
      ) : dashboard.isError ? null : <TableSkeleton rows={4} columns={5} />}
      <MyCash standalone={false} />
      {data ? <AwaitingConfirmation transfers={data.awaitingConfirmation} currency={currency} today={today} /> : null}

      <TransactionHistory
        from={filters.from}
        to={filters.to}
        scope={historyScope}
        currency={currency}
        timezone={timezone}
        locations={locations}
      />

      {data ? (
        <>
          <Unassigned data={data} />
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Position data={data} />
            <Collections data={data} />
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Card>
              <h2 className="text-sm font-semibold text-slate-900">Held with riders — not in any account</h2>
              <p className="mt-0.5 text-xs text-slate-500">Rider money stays separate until the rider hands it over.</p>
              <dl className="mt-2 space-y-1 text-sm" data-testid="rider-held">
                <div className="flex justify-between gap-3">
                  <dt>Confirmed as received by riders, not yet returned</dt>
                  <dd className="whitespace-nowrap font-semibold tabular-nums">{m(data.riderHeld.confirmed)}</dd>
                </div>
                {data.riderHeld.riders.slice(0, 8).map((r) => (
                  <div key={r.riderId} className="flex justify-between gap-3 pl-3 text-slate-600">
                    <dt>{r.riderName}</dt>
                    <dd className="whitespace-nowrap tabular-nums">{m(r.amount)}</dd>
                  </div>
                ))}
                <div className="flex justify-between gap-3 border-t border-slate-100 pt-1 text-amber-800">
                  <dt>Recorded as with riders in this period, nobody confirmed (Cash to Rider, synced cash)</dt>
                  <dd className="whitespace-nowrap tabular-nums">{m(data.riderHeld.unconfirmed)}</dd>
                </div>
              </dl>
              {(data.riderHeld.unconfirmedByRider ?? []).length > 0 ? (
                <div className="-mx-3 mt-2 overflow-x-auto px-3">
                  <table className="w-full min-w-[28rem] text-sm" data-testid="rider-held-by-rider">
                    <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="py-1 pr-3 font-medium">Rider</th>
                        <th className="py-1 pr-3 text-right font-medium">Took from customers</th>
                        <th className="py-1 pr-3 text-right font-medium">Their pay</th>
                        <th className="py-1 text-right font-medium">Company&apos;s part owed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.riderHeld.unconfirmedByRider.map((r) => (
                        <tr key={`${r.riderId ?? ''}-${r.riderName}`}>
                          <td className="py-1 pr-3">{r.riderName} <span className="text-xs text-slate-400">· {r.rides}</span></td>
                          <td className="whitespace-nowrap py-1 pr-3 text-right tabular-nums">{m(r.held)}</td>
                          <td className="whitespace-nowrap py-1 pr-3 text-right tabular-nums">{m(r.riderShare)}</td>
                          <td className="whitespace-nowrap py-1 text-right font-medium tabular-nums">{m(r.companyShare)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-1 text-xs text-slate-500">
                    Not the company&apos;s cash yet: the company&apos;s part is owed by the rider until it is taken from their earnings or handed over.
                  </p>
                </div>
              ) : null}
            </Card>
          </div>

          <Accounts
            accounts={data.accounts}
            openingAsOf={data.summary.openingAsOf}
            from={data.from}
            to={data.to}
            onView={setViewing}
            onOpening={mayManage ? setOpening : undefined}
          />
        </>
      ) : null}
      <CashFlowCard from={filters.from} to={filters.to} area={area} currency={currency} />
      {data ? <StatementsCard accounts={data.accounts} currency={currency} mayManage={mayManage} today={today} /> : null}

      <Transfers transfers={transfers.data ?? []} currency={currency} mayManage={mayManage} today={today} />
      {journal ? <JournalModal filters={filters} currency={currency} onClose={() => setJournal(false)} /> : null}
      {opening ? <OpeningModal account={opening} today={today} onClose={() => setOpening(null)} /> : null}

      {creating ? <CreateAccountModal members={members.data ?? []} onClose={() => setCreating(false)} /> : null}
      {moving ? <TransferModal accounts={live} mayManage={mayManage} toKind={moving.toKind} onClose={() => setMoving(null)} /> : null}
      {viewing && data ? (
        <MovementsModal account={viewing} from={data.from} to={data.to} openingAsOf={data.summary.openingAsOf} onClose={() => setViewing(null)} />
      ) : null}
    </div>
  );
}

interface PanelRow {
  key: string;
  name: string;
  amount: string;
  /** Under the name: who holds it, or why it is not in an account. */
  detail?: string | undefined;
  warn?: boolean | undefined;
}

/** A share of the panel's total, or a dash where a share would mislead (nothing held, or a negative). */
function shareOf(amount: string, total: string): string {
  const a = Number(amount);
  const t = Number(total);
  if (!(t > 0) || a <= 0) return '—';
  return `${((a / t) * 100).toFixed(1)}%`;
}

/**
 * Physical cash by location. Each location is its cash accounts, the cash
 * recorded there that no account claims yet, and cash it sent that the
 * receiver has not confirmed — still counted where it left, never in the bank
 * until it arrives. The panel's total is the sum of these rows, nothing more.
 */
export function cashPanelRows(data: CashBankDashboard): PanelRow[] {
  const m = (v: string) => money(v, data.currencyCode);
  if (data.cashByLocation.length > 0) {
    return data.cashByLocation.map((c) => {
      const notes = [
        ...c.holders.filter((h) => h.holderName).map((h) => `${h.holderName}: ${m(h.balance)}`),
        ...(c.unassigned !== '0.00' ? [`${m(c.unassigned)} not in an account yet`] : []),
        ...(c.sentAwaitingConfirmation !== '0.00' ? [`${m(c.sentAwaitingConfirmation)} sent, awaiting confirmation`] : []),
      ];
      return {
        key: c.branchId ?? 'none',
        name: c.branchName ?? 'Whole business',
        amount: c.total,
        detail: notes.join(' · ') || undefined,
        warn: c.unassigned !== '0.00' || c.sentAwaitingConfirmation !== '0.00',
      };
    });
  }
  // Narrowed to an account or a worker: their cash accounts, by location.
  const by = new Map<string, PanelRow>();
  for (const a of data.accounts.filter((x) => x.kind === 'cash')) {
    const key = a.branchId ?? 'none';
    const row = by.get(key) ?? { key, name: a.branchName ?? 'Whole business', amount: '0.00' };
    row.amount = addAmounts(row.amount, a.balance);
    by.set(key, row);
  }
  return [...by.values()];
}

/** Bank and wallet money by account, plus any recorded as paid electronically that no account claims. */
export function bankPanelRows(data: CashBankDashboard): PanelRow[] {
  const rows: PanelRow[] = data.accounts
    .filter((a) => a.kind !== 'cash' && !(a.archived && a.balance === '0.00'))
    .map((a) => ({
      key: a.id,
      name: a.name,
      amount: a.balance,
      detail: [MONEY_ACCOUNT_KIND_LABELS[a.kind], a.branchName, a.archived ? 'closed' : null].filter(Boolean).join(' · '),
    }));
  if (data.unassigned.bank !== '0.00') {
    rows.push({
      key: 'unassigned',
      name: 'Not in a bank account yet',
      amount: data.unassigned.bank,
      detail: 'Recorded as paid electronically; no account claims it, so no bank has confirmed it',
      warn: true,
    });
  }
  return rows;
}

function MoneyPanel({
  title,
  testId,
  sub,
  rows,
  firstColumn,
  currency,
  recordLabel,
  onRecord,
  footer,
  period,
}: {
  title: string;
  testId: string;
  sub: string;
  rows: PanelRow[];
  firstColumn: string;
  currency: string;
  recordLabel: string;
  onRecord?: (() => void) | undefined;
  footer?: ReactNode;
  period?: ReactNode;
}): ReactNode {
  const m = (v: string) => money(v, currency);
  const total = rows.reduce((t, r) => addAmounts(t, r.amount), '0.00');
  return (
    <Card>
      <div data-testid={testId}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</h2>
            <p className="mt-1 overflow-x-auto whitespace-nowrap text-2xl font-semibold tabular-nums text-slate-900" data-testid={`${testId}-total`}>
              {m(total)}
            </p>
            <p className="text-xs text-slate-500">{sub}</p>
          </div>
          {onRecord ? <Button variant="secondary" size="sm" onClick={onRecord}>{recordLabel}</Button> : null}
        </div>
        {period}
        {rows.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">Nothing held here yet.</p>
        ) : (
          <div className="-mx-3 mt-3 overflow-x-auto px-3">
            <table className="w-full min-w-[20rem] text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-1 pr-3 font-medium">{firstColumn}</th>
                  <th className="py-1 pr-3 text-right font-medium">Amount</th>
                  <th className="py-1 text-right font-medium">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td className="py-1.5 pr-3">
                      <span className={cn('font-medium', r.warn ? 'text-amber-800' : 'text-slate-900')}>{r.name}</span>
                      {r.detail ? <span className="block text-xs text-slate-500">{r.detail}</span> : null}
                    </td>
                    <td className={cn('whitespace-nowrap py-1.5 pr-3 text-right tabular-nums', Number(r.amount) < 0 ? 'text-rose-700' : '')}>{m(r.amount)}</td>
                    <td className="whitespace-nowrap py-1.5 text-right text-xs tabular-nums text-slate-500">{shareOf(r.amount, total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {footer}
      </div>
    </Card>
  );
}

/**
 * How a panel's balance moved over the chosen dates: what it held the day
 * before they began, what came in and went out during them, what moved between
 * the company's own accounts, and what it held at their end — the panel's
 * total. The opening and closing are each worked out on their own, so any
 * amount the movements do not explain is shown, never hidden.
 */
export function PeriodLines({
  figures,
  openingAsOf,
  from,
  to,
  currency,
  testId,
}: {
  figures: CashBankPeriodFigures;
  openingAsOf: string;
  from: string;
  to: string;
  currency: string;
  testId: string;
}): ReactNode {
  const m = (v: string) => money(v, currency);
  const range = from === to ? dayLabel(from) : `${dayLabel(from)} to ${dayLabel(to)}`;
  const lines: Array<[string, string, string]> = [
    ['opening', `Opening balance — as of ${dayLabel(openingAsOf)}`, m(figures.opening)],
    ['in', `Money in — ${range}`, m(figures.moneyIn)],
    ['out', `Money out — ${range}`, m(figures.moneyOut)],
    ['net', `Net movement — ${range}`, m(subtractAmounts(figures.moneyIn, figures.moneyOut))],
  ];
  if (figures.transfersIn !== '0.00') lines.push(['transfers-in', 'Moved in from your other accounts', m(figures.transfersIn)]);
  if (figures.transfersOut !== '0.00') lines.push(['transfers-out', 'Moved out to your other accounts', m(figures.transfersOut)]);
  return (
    <dl className="mt-2 space-y-0.5 border-t border-slate-100 pt-2 text-xs" data-testid={testId}>
      {lines.map(([key, label, value]) => (
        <div key={key} className="flex justify-between gap-3 text-slate-600" data-testid={`${testId}-${key}`}>
          <dt>{label}</dt>
          <dd className="whitespace-nowrap tabular-nums">{value}</dd>
        </div>
      ))}
      <div className="flex justify-between gap-3 font-semibold text-slate-900" data-testid={`${testId}-closing`}>
        <dt>Closing balance — as of {dayLabel(to)}</dt>
        <dd className="whitespace-nowrap tabular-nums">{m(figures.closing)}</dd>
      </div>
      {figures.difference !== '0.00' ? (
        <p role="alert" className="pt-1 text-rose-700" data-testid={`${testId}-difference`}>
          The movements do not add up to the closing balance: {m(figures.difference)} is not explained by them. The balances
          shown are each worked out from the records; please report this so it can be looked into.
        </p>
      ) : null}
    </dl>
  );
}

function CashPanel({ data, onRecord }: { data: CashBankDashboard; onRecord?: (() => void) | undefined }): ReactNode {
  return (
    <MoneyPanel
      title="Total cash"
      testId="cash-panel"
      sub={`Closing balance — physical cash at each location, as of ${dayLabel(data.asOf)}`}
      period={
        <PeriodLines
          figures={data.summary.cash}
          openingAsOf={data.summary.openingAsOf}
          from={data.from}
          to={data.to}
          currency={data.currencyCode}
          testId="cash-period"
        />
      }
      rows={cashPanelRows(data)}
      firstColumn="Location"
      currency={data.currencyCode}
      recordLabel="Record cash"
      onRecord={onRecord}
    />
  );
}

function BankPanel({ data, onRecord }: { data: CashBankDashboard; onRecord?: (() => void) | undefined }): ReactNode {
  return (
    <MoneyPanel
      title="Total bank"
      testId="bank-panel"
      sub={`Closing balance — bank and wallet accounts, as of ${dayLabel(data.asOf)}`}
      period={
        <PeriodLines
          figures={data.summary.bank}
          openingAsOf={data.summary.openingAsOf}
          from={data.from}
          to={data.to}
          currency={data.currencyCode}
          testId="bank-period"
        />
      }
      rows={bankPanelRows(data)}
      firstColumn="Account"
      currency={data.currencyCode}
      recordLabel="Record bank"
      onRecord={onRecord}
      footer={
        data.totals.depositsInTransit !== '0.00' ? (
          <p className="mt-2 text-xs text-amber-800">
            {money(data.totals.depositsInTransit, data.currencyCode)} is on its way from cash and not confirmed at the bank:
            counted in cash where it left, not here.
          </p>
        ) : null
      }
    />
  );
}

/** Money recorded but not counted as funds, and deposits waiting at the bank. */
function Attention({ data }: { data: CashBankDashboard }): ReactNode {
  const m = (v: string) => money(v, data.currencyCode);
  const items: Array<[string, string]> = [];
  if (data.deposits.pending.count > 0) {
    items.push(['Deposits waiting to be confirmed at the bank', `${data.deposits.pending.count} · ${m(data.deposits.pending.amount)}`]);
  }
  if (data.unverified.count > 0) {
    items.push(['Bank payments awaiting verification (not counted)', `${data.unverified.count} · ${m(data.unverified.amount)}`]);
  }
  if (data.unpaidExpenses.count > 0) {
    items.push([
      'Expenses recorded, not paid yet (no money moved)',
      `${data.unpaidExpenses.count} · ${m(data.unpaidExpenses.amount)}${data.unpaidExpenses.due.count > 0 ? ` (${data.unpaidExpenses.due.count} due)` : ''}`,
    ]);
  }
  if (items.length === 0) return null;
  return (
    <Card>
      <dl className="space-y-1 text-sm" data-testid="cash-bank-attention">
        {items.map(([label, value]) => (
          <div key={label} className="flex flex-wrap justify-between gap-x-3">
            <dt className="text-amber-800">{label}</dt>
            <dd className="whitespace-nowrap tabular-nums text-amber-800">{value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function Unassigned({ data }: { data: CashBankDashboard }): ReactNode {
  const m = (v: string) => money(v, data.currencyCode);
  if (data.unassigned.cash === '0.00' && data.unassigned.bank === '0.00') return null;
  return (
    <Card>
      <div className="space-y-2 text-sm" data-testid="unassigned">
        <h2 className="font-semibold text-amber-800">
          Not in an account: cash <span className="whitespace-nowrap">{m(data.unassigned.cash)}</span> · bank{' '}
          <span className="whitespace-nowrap">{m(data.unassigned.bank)}</span>
        </h2>
        <p className="text-xs text-slate-600">
          Recorded payments and expenses that named no account, at a location with no default account of that kind. They
          are real money movements, not missing deposits: add the cash and bank accounts for each location (or mark one as
          its default) and they land there — nothing is invented or posted twice.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[32rem] text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-1 pr-3">Kind</th>
                <th className="py-1 pr-3">Location</th>
                <th className="py-1 pr-3">Source</th>
                <th className="py-1 pr-3 text-right">Records</th>
                <th className="py-1 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.unassigned.bySource.map((u) => (
                <tr key={`${u.kind}-${u.branchName}-${u.source}`}>
                  <td className="py-1 pr-3">{u.kind === 'cash' ? 'Cash' : 'Bank'}</td>
                  <td className="py-1 pr-3">{u.branchName ?? 'No location'}</td>
                  <td className="py-1 pr-3">{SOURCE_LABELS[u.source] ?? u.source}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{u.count}</td>
                  <td className="whitespace-nowrap py-1 text-right tabular-nums">{m(u.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}

function Accounts({
  accounts,
  openingAsOf,
  from,
  to,
  onView,
  onOpening,
}: {
  accounts: CashBankAccountView[];
  openingAsOf: string;
  from: string;
  to: string;
  onView: (a: CashBankAccountView) => void;
  onOpening?: ((a: MoneyAccountView) => void) | undefined;
}): ReactNode {
  if (accounts.length === 0) {
    return (
      <EmptyState
        title="No accounts yet"
        description="Add the cash held at each location or by each worker, and the bank accounts payments go into."
      />
    );
  }
  return (
    <Card>
      <h2 className="mb-2 text-sm font-semibold text-slate-900">Who holds what</h2>
      <div className="-mx-3 overflow-x-auto px-3">
        <table className="w-full min-w-[1040px] text-sm" data-testid="cash-bank-accounts">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-3 font-medium">Account</th>
              <th className="py-2 pr-3 font-medium">Kind</th>
              <th className="py-2 pr-3 font-medium">Location</th>
              <th className="py-2 pr-3 font-medium">Held by</th>
              <th className="py-2 pr-3 text-right font-medium">Opening<span className="block normal-case">{dayLabel(openingAsOf)}</span></th>
              <th className="py-2 pr-3 text-right font-medium">In<span className="block normal-case">{dayLabel(from)} – {dayLabel(to)}</span></th>
              <th className="py-2 pr-3 text-right font-medium">Out<span className="block normal-case">{dayLabel(from)} – {dayLabel(to)}</span></th>
              <th className="py-2 pr-3 text-right font-medium">Transfers<span className="block normal-case">own accounts</span></th>
              <th className="py-2 pr-3 text-right font-medium">Closing<span className="block normal-case">{dayLabel(to)}</span></th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {accounts.map((a) => (
              <tr key={a.id} className={a.archived ? 'text-slate-400' : undefined}>
                <td className="min-w-[14rem] py-2 pr-3 font-medium text-slate-900">
                  {a.name} {a.isDefault ? <Badge tone="info">Default</Badge> : null}
                  {a.archived ? <Badge tone="neutral">Closed</Badge> : null}
                  {!a.openingOn && !a.archived ? (
                    <p className="text-xs font-normal text-amber-700">No opening date: counts every movement since records began</p>
                  ) : (
                    <p className="text-xs font-normal text-slate-400">Opened with {money(a.openingBalance, a.currencyCode)} on {a.openingOn}</p>
                  )}
                </td>
                <td className="py-2 pr-3">{MONEY_ACCOUNT_KIND_LABELS[a.kind]}</td>
                <td className="py-2 pr-3">{a.branchName ?? 'Whole business'}</td>
                <td className="py-2 pr-3">{a.holderName ?? '—'}</td>
                <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">{money(a.period.opening, a.currencyCode)}</td>
                <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-emerald-700">{money(a.period.moneyIn, a.currencyCode)}</td>
                <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-rose-700">{money(a.period.moneyOut, a.currencyCode)}</td>
                <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                  {money(subtractAmounts(a.period.transfersIn, a.period.transfersOut), a.currencyCode)}
                </td>
                <td className="whitespace-nowrap py-2 pr-3 text-right font-semibold tabular-nums">
                  {money(a.balance, a.currencyCode)}
                  {a.period.difference !== '0.00' ? (
                    <span role="alert" className="block text-xs font-normal text-rose-700">
                      {money(a.period.difference, a.currencyCode)} not explained by its movements
                    </span>
                  ) : null}
                </td>
                <td className="whitespace-nowrap py-2 text-right">
                  {onOpening ? <Button variant="ghost" size="sm" onClick={() => onOpening(a)}>Opening</Button> : null}
                  <Button variant="ghost" size="sm" onClick={() => onView(a)}>Movements</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Transfers({
  transfers,
  currency,
  mayManage,
  today,
}: {
  transfers: MoneyTransferView[];
  currency: string;
  mayManage: boolean;
  today: string;
}): ReactNode {
  const voidTransfer = useVoidTransfer();
  const [voiding, setVoiding] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  if (transfers.length === 0) return null;
  return (
    <section>
      <h2 className="mb-2.5 text-sm font-semibold text-slate-900">Handovers, deposits and transfers</h2>
      <Card>
        <ul className="divide-y divide-slate-100" data-testid="transfers">
          {transfers.map((t) => (
            <li key={t.id} className="py-2.5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className={t.voided ? 'text-slate-400 line-through' : undefined}>
                  <p className="text-sm font-medium">
                    <span className="whitespace-nowrap">{money(t.amount, currency)}</span> · {TRANSFER_KIND_LABEL[t.kind]} · {t.fromAccountName} → {t.toAccountName}{' '}
                    {!t.voided && t.status === 'pending' ? <Badge tone="warning">Waiting</Badge> : null}
                  </p>
                  <p className="text-xs text-slate-500">
                    Sent {t.transferredOn}
                    {t.completedOn ? ` · arrived ${t.completedOn}` : ''}
                    {t.submittedBy ? ` · handed over by ${t.submittedBy}` : ''}
                    {t.receiverName ? ` · to ${t.receiverName}` : ''}
                    {t.handledByName ? ` · handled by ${t.handledByName}` : ''}
                    {t.reference ? ` · ref ${t.reference}` : ''}
                    {t.completionReference ? ` · bank ref ${t.completionReference}` : ''}
                    {t.evidenceAttachmentId ? ' · receipt attached' : ''}
                    {t.recordedBy ? ` · recorded by ${t.recordedBy}` : ''}
                    {t.completedBy && t.completedBy !== t.recordedBy ? ` · confirmed by ${t.completedBy}` : ''}
                  </p>
                </div>
                {t.voided ? (
                  <span className="text-xs text-red-700">Voided: {t.voidReason}</span>
                ) : t.status === 'pending' ? (
                  <ConfirmOrDecline transfer={t} today={today} toBank={t.kind === 'deposit' || t.kind === 'transfer'} mayCancel={t.mayCancel} />
                ) : mayManage && voiding !== t.id ? (
                  <Button variant="ghost" size="sm" onClick={() => { setVoiding(t.id); setReason(''); }}>Void</Button>
                ) : null}
              </div>
              {voiding === t.id ? (
                <form
                  className="mt-2 flex items-end gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    voidTransfer.mutate({ id: t.id, reason }, { onSuccess: () => setVoiding(null) });
                  }}
                >
                  <div className="flex-1">
                    <Input label="Why is it void?" value={reason} onChange={(e) => setReason(e.target.value)} />
                  </div>
                  <Button type="button" variant="secondary" size="sm" onClick={() => setVoiding(null)}>Cancel</Button>
                  <Button type="submit" variant="danger" size="sm" loading={voidTransfer.isPending} disabled={reason.trim().length < 3}>
                    Void transfer
                  </Button>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

function CreateAccountModal({ members, onClose }: { members: MemberOption[]; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const create = useCreateAccount();
  const { locations } = useWorkingLocation();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<MoneyAccountKind>('cash');
  const [branchId, setBranchId] = useState(locations[0]?.id ?? '');
  const [isDefault, setIsDefault] = useState(true);
  const [openingBalance, setOpeningBalance] = useState('');
  // Counted from today unless told otherwise: an account with no date takes in every movement ever recorded.
  const [openingOn, setOpeningOn] = useState(todayIn(business?.timezone ?? 'UTC'));
  const [holderUserId, setHolderUserId] = useState('');
  const [holderName, setHolderName] = useState('');

  return (
    <Modal open onClose={onClose} title="Add an account">
      <form
        className="space-y-4"
        aria-label="Add an account"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            {
              name,
              kind,
              branchId: branchId || null,
              isDefault,
              ...(openingBalance ? { openingBalance } : {}),
              ...(openingOn ? { openingOn } : {}),
              ...(kind === 'cash' && holderUserId ? { holderUserId } : {}),
              ...(kind === 'cash' && !holderUserId && holderName.trim() ? { holderName: holderName.trim() } : {}),
            },
            { onSuccess: onClose },
          );
        }}
      >
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Holy Ghost till, Chidi's float, Moniepoint, GTBank" />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as MoneyAccountKind)}
            options={MONEY_ACCOUNT_KINDS.map((k) => ({ value: k, label: MONEY_ACCOUNT_KIND_LABELS[k] }))}
          />
          <Select
            label="Location"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            options={[{ value: '', label: 'Whole business' }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
          />
          {kind === 'cash' ? (
            <>
              <Select
                label="Held by (worker)"
                value={holderUserId}
                onChange={(e) => setHolderUserId(e.target.value)}
                options={[{ value: '', label: 'Nobody in particular' }, ...members.map((mb) => ({ value: mb.userId, label: mb.fullName ?? mb.email ?? 'Member' }))]}
              />
              {!holderUserId ? (
                <Input label="Or holder's name" value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Not a CenoX user" />
              ) : (
                <span />
              )}
            </>
          ) : null}
          <Input label="Opening balance" inputMode="decimal" value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} hint="What it held before CenoX recorded it" />
          <Input
            label="Counted from"
            type="date"
            value={openingOn}
            onChange={(e) => setOpeningOn(e.target.value)}
            hint="The day the opening balance was true. Movements before it are inside the opening balance."
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Default for {kind === 'cash' ? 'cash' : 'non-cash'} payments at this location that name no account
        </label>
        {create.error ? (
          <p role="alert" className="text-sm text-red-700">{create.error instanceof ApiError ? create.error.message : 'Could not add the account.'}</p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={create.isPending} disabled={name.trim().length === 0 || !openingOn}>Add account</Button>
        </div>
      </form>
    </Modal>
  );
}

function TransferModal({
  accounts,
  mayManage,
  toKind,
  onClose,
}: {
  accounts: MoneyAccountView[];
  mayManage: boolean;
  /** Start with a destination of this kind: cash for a handover, bank for a deposit. */
  toKind?: 'cash' | 'bank' | undefined;
  onClose: () => void;
}): ReactNode {
  const { business, user } = useAuth();
  const today = todayIn(business?.timezone ?? 'UTC');
  const record = useRecordTransfer();
  const upload = useUploadEvidence();
  const [key] = useState(newSubmissionKey);
  const [fromAccountId, setFrom] = useState(accounts.find((a) => a.kind === 'cash')?.id ?? accounts[0]?.id ?? '');
  const [toAccountId, setTo] = useState(
    (toKind === 'cash'
      ? accounts.find((a) => a.kind === 'cash' && a.id !== fromAccountId)
      : accounts.find((a) => a.kind !== 'cash'))?.id ??
      accounts.find((a) => a.id !== fromAccountId)?.id ??
      '',
  );
  const [amount, setAmount] = useState('');
  const [transferredOn, setOn] = useState(today);
  const [reference, setReference] = useState('');
  const [handledByName, setHandled] = useState('');
  const [arrived, setArrived] = useState(mayManage);
  const [evidenceId, setEvidenceId] = useState<string | null>(null);
  const options = accounts.map((a) => ({ value: a.id, label: `${a.name} (${money(a.balance, a.currencyCode)})` }));
  const from = accounts.find((a) => a.id === fromAccountId);
  const to = accounts.find((a) => a.id === toAccountId);
  const isDeposit = from?.kind === 'cash' && to?.kind !== 'cash';
  // Cash into another person's hands waits for them to confirm it; nobody confirms it for them.
  const toSomeoneElse = from?.kind === 'cash' && to?.kind === 'cash' && Boolean(to.holderUserId) && to.holderUserId !== user?.id;
  // Recording a worker's handover for them: it is theirs, not the recorder's.
  const onBehalfOf = from?.holderUserId && from.holderUserId !== user?.id ? from.holderUserId : null;
  const [secondOne, setSecondOne] = useState(false);
  const duplicate = record.error instanceof ApiError && record.error.status === 409 && Boolean(record.error.fields?.confirmDuplicate);

  return (
    <Modal open onClose={onClose} title="Move money">
      <form
        className="space-y-4"
        aria-label="Move money"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          record.mutate(
            {
              fromAccountId,
              toAccountId,
              amount,
              transferredOn,
              idempotencyKey: key,
              pending: toSomeoneElse || !arrived,
              ...(onBehalfOf && mayManage ? { submittedByUserId: onBehalfOf } : {}),
              ...(secondOne ? { confirmDuplicate: true } : {}),
              ...(reference ? { reference } : {}),
              ...(handledByName.trim() ? { handledByName: handledByName.trim() } : {}),
              ...(evidenceId ? { evidenceAttachmentId: evidenceId } : {}),
            },
            { onSuccess: onClose },
          );
        }}
      >
        <p className="text-sm text-slate-600">
          One account goes down and the other goes up by the same amount — a deposit, a withdrawal or a transfer. It is
          never revenue or an expense. Part of the cash may be banked; the rest stays where it is.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Select label="From" value={fromAccountId} onChange={(e) => setFrom(e.target.value)} options={options} />
          <Select label="To" value={toAccountId} onChange={(e) => setTo(e.target.value)} options={options} />
          <Input label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Input label="Date sent" type="date" max={today} value={transferredOn} onChange={(e) => setOn(e.target.value)} />
          <Input label="Handled by" value={handledByName} onChange={(e) => setHandled(e.target.value)} placeholder="Who carried or sent it" />
          <Input label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Deposit slip or transfer number" />
        </div>
        <div className="text-sm">
          <label className="block text-xs font-medium text-slate-600" htmlFor="move-money-evidence">Receipt or deposit slip</label>
          <input
            id="move-money-evidence"
            type="file"
            accept="image/*,application/pdf"
            className="mt-1 text-sm"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f, { onSuccess: (r) => setEvidenceId(r.id) });
            }}
          />
          {upload.isPending ? <p className="text-xs text-slate-500">Uploading…</p> : null}
          {evidenceId ? <p className="text-xs text-emerald-700">Receipt attached.</p> : null}
          {upload.error ? <p className="text-xs text-red-700">{upload.error.message}</p> : null}
        </div>
        {toSomeoneElse ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
            A handover: it leaves “{from?.name}” now and is {to?.holderName ?? 'the receiver'}&apos;s once they confirm they received it.
          </p>
        ) : mayManage ? (
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" className="mt-1" checked={arrived} onChange={(e) => setArrived(e.target.checked)} />
            <span>
              It has arrived in “{to?.name ?? 'the destination'}”. Untick while {isDeposit ? 'the cash is on its way to the bank' : 'it is not confirmed yet'}:
              it leaves “{from?.name ?? 'the source'}” now and lands only when someone confirms it.
            </span>
          </label>
        ) : (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
            Your deposit stays pending until someone who reconciles cash confirms it arrived.
          </p>
        )}
        {duplicate ? (
          <label className="flex items-start gap-2 text-sm text-amber-900">
            <input type="checkbox" className="mt-1" checked={secondOne} onChange={(e) => setSecondOne(e.target.checked)} />
            <span>This is a second, separate amount — not the same money entered again.</span>
          </label>
        ) : null}
        {record.error ? (
          <p role="alert" className="text-sm text-red-700">{record.error instanceof ApiError ? record.error.message : 'Could not record it.'}</p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={record.isPending} disabled={!amount || !fromAccountId || fromAccountId === toAccountId || (duplicate && !secondOne)}>Move money</Button>
        </div>
      </form>
    </Modal>
  );
}

function MovementsModal({
  account,
  from,
  to,
  openingAsOf,
  onClose,
}: {
  account: CashBankAccountView;
  from: string;
  to: string;
  openingAsOf: string;
  onClose: () => void;
}): ReactNode {
  // Before the account's opening date its movements are inside the opening balance, so the list starts there.
  const start = account.openingOn && account.openingOn > from ? account.openingOn : from;
  const movements = useAccountMovements(account.id, { from: start, to });
  const m = (v: string) => money(v, account.currencyCode);
  const rows = movements.data?.movements ?? [];
  const complete = movements.data?.complete ?? true;
  const listed = rows.reduce((t, r) => addAmounts(t, r.amount), '0.00');
  const moved = subtractAmounts(account.balance, account.period.opening);
  return (
    <Modal open onClose={onClose} title={`${account.name}: movements ${dayLabel(start)} to ${dayLabel(to)}`}>
      {movements.isPending ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="space-y-2" data-testid="account-history">
          <div className="flex justify-between gap-3 text-sm font-medium text-slate-700" data-testid="account-history-opening">
            <span>
              {start > from ? `Opening balance — recorded for ${dayLabel(start)}` : `Opening balance — as of ${dayLabel(openingAsOf)}`}
            </span>
            <span className="whitespace-nowrap tabular-nums">{m(account.period.opening)}</span>
          </div>
          {rows.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing moved through this account in these dates.</p>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {rows.map((mv, i) => (
                    <tr key={`${mv.source}-${mv.ref}-${i}`}>
                      <td className="py-1.5 pr-3 text-xs text-slate-500">{mv.on}</td>
                      <td className="py-1.5 pr-3">{SOURCE_LABELS[mv.source] ?? mv.source}</td>
                      <td className={`whitespace-nowrap py-1.5 text-right tabular-nums ${Number(mv.amount) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {m(mv.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-between gap-3 border-t border-slate-200 pt-2 text-sm font-semibold text-slate-900" data-testid="account-history-closing">
            <span>Closing balance — as of {dayLabel(to)}</span>
            <span className="whitespace-nowrap tabular-nums">{m(account.balance)}</span>
          </div>
          {!complete ? (
            <p role="alert" className="text-xs text-amber-800" data-testid="account-history-incomplete">
              These dates hold more movements than can be listed at once, so the list above is incomplete and does not add up
              to the change in the balance. The opening and closing balances are accurate: they are worked out from every
              movement. Choose shorter dates to see each one.
            </p>
          ) : listed !== moved ? (
            <p role="alert" className="text-xs text-rose-700" data-testid="account-history-difference">
              The movements listed add up to {m(listed)}, but the balance moved by {m(moved)}. Please report this so it can be
              looked into.
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

/**
 * What an account held when CenoX started counting it, from the bank
 * statement or a cash count, and from which day. Movements before that day are
 * inside it. Changing it moves the balance on every day since, so it asks why.
 */
function OpeningModal({ account, today, onClose }: { account: MoneyAccountView; today: string; onClose: () => void }): ReactNode {
  const update = useUpdateAccount();
  // An account with no date was never given a balance: it is typed from the statement, never assumed to be 0.
  const [openingBalance, setBalance] = useState(account.openingOn ? account.openingBalance : '');
  const [openingOn, setOn] = useState(account.openingOn ?? today);
  const [reason, setReason] = useState('');
  return (
    <Modal open onClose={onClose} title={`${account.name}: opening balance`}>
      <form
        className="space-y-4"
        aria-label="Opening balance"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate({ id: account.id, openingBalance, openingOn, reason: reason.trim() }, { onSuccess: onClose });
        }}
      >
        <p className="text-sm text-slate-600">
          {account.kind === 'cash'
            ? 'What was physically counted in this cash at the start of the day.'
            : 'The balance on the bank statement at the start of the day.'}{' '}
          Money recorded before that day is inside this figure and is not counted again; money recorded from that day on is
          added to it. Nothing is booked: only where the account starts counting changes.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Opening balance" inputMode="decimal" value={openingBalance} onChange={(e) => setBalance(e.target.value)} />
          <Input label="Counted from" type="date" max={today} value={openingOn} onChange={(e) => setOn(e.target.value)} />
        </div>
        <Input label="Why, and from what" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="GTBank statement of 26 Oct, page 1" />
        {update.error ? <p role="alert" className="text-sm text-red-700">{update.error.message}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={update.isPending} disabled={!openingBalance || !openingOn || reason.trim().length < 3}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
